<script setup lang="ts">
import type { ContentScope, ContentScopeSubject } from '@/types/contentScope'

const props = withDefaults(
  defineProps<{
    subject: ContentScopeSubject
    error?: boolean
    withLabel?: boolean
  }>(),
  {
    error: false,
    withLabel: true
  }
)

const scope = defineModel<ContentScope | null>({ default: null })

const { t } = useI18n()
const labelId = useId()

const options = computed(() => [
  {
    value: 'network' as const,
    label: t(`contentScope.subjects.${props.subject}.networkLabel`),
    description: t(`contentScope.subjects.${props.subject}.networkDescription`)
  },
  {
    value: 'local' as const,
    label: t(`contentScope.subjects.${props.subject}.localLabel`),
    description: t(`contentScope.subjects.${props.subject}.localDescription`)
  }
])
</script>

<template>
  <div class="flex flex-col gap-3 text-left" data-testid="content-scope-question">
    <span v-if="withLabel" :id="labelId" class="text-sm font-medium text-foreground">
      {{ t(`contentScope.subjects.${subject}.fieldTitle`) }}
    </span>
    <p class="text-sm text-muted-foreground">{{ t(`contentScope.subjects.${subject}.description`) }}</p>
    <RadioGroup
      v-model="scope"
      :aria-labelledby="withLabel ? labelId : undefined"
      :aria-label="withLabel ? undefined : t(`contentScope.subjects.${subject}.fieldTitle`)"
      class="grid grid-cols-1 gap-3"
    >
      <label
        v-for="option in options"
        :key="option.value"
        class="flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50"
        :class="[scope === option.value && 'border-primary bg-primary/5', error && 'border-destructive']"
        :data-testid="`content-scope-option-${option.value}`"
      >
        <RadioGroupItem :value="option.value" class="mt-0.5" />
        <span class="flex flex-col gap-1">
          <span class="text-sm font-medium">{{ option.label }}</span>
          <span class="text-xs text-muted-foreground">{{ option.description }}</span>
          <span class="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            {{ t('contentScope.field.originLine') }}
            <ContentScopeBadge :scope="option.value" />
          </span>
        </span>
      </label>
    </RadioGroup>
    <p v-if="error" class="text-xs font-medium text-destructive" role="alert">
      {{ t('contentScope.field.required') }}
    </p>
  </div>
</template>
